import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

/**
 * GroupsApiService (Angular 18) — RBAC user groups + user/role mappings.
 *
 * Replaces apps/b2b-ui/common/services/api/groupsApi/groupsApi.service.js.
 *
 * Four sub-namespaces:
 *   apiService.groups.users.list                              users in a group
 *   apiService.groups.roles.list                              roles in a group
 *   apiService.groups.userMaps.{list,detail,create,update,batchCreate,remove}
 *   apiService.groups.roleMaps.{list,detail,create,update,batchCreate,remove}
 */
const GROUPS_BASE_V1 = '/v1/api/rbac/user/groups';
const GROUPS_BASE_V2 = '/v2/api/rbac/user/groups';
const USER_MAP_BASE_V1 = '/v1/api/rbac/user/group/map';
const USER_MAP_BASE_V2 = '/v2/api/rbac/user/group/map';
const ROLE_MAP_BASE_V1 = '/v1/api/rbac/user/group/roles';
const ROLE_MAP_BASE_V2 = '/v2/api/rbac/user/group/roles';

interface GroupPayload {
  id?: number | string;
  name: string;
  displayName?: string;
}

interface UserMapPayload {
  id?: number | string;
  userId: number | string;
  userGroupId: number | string;
}

interface RoleMapPayload {
  id?: number | string;
  roleId: number | string;
  userGroupId: number | string;
}

@Injectable({ providedIn: 'root' })
export class GroupsApiService {
  private readonly http = inject(HttpClient);

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(GROUPS_BASE_V2, {
        params: buildParams(props, PAGINATION_SEARCH_FIELDS),
      }),
    );
  }

  detail(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${GROUPS_BASE_V1}/${id}`));
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${GROUPS_BASE_V1}/${id}`));
  }

  create(props: GroupPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(GROUPS_BASE_V1, {
        name: props.name,
        displayName: props.displayName,
      }),
    );
  }

  update(props: GroupPayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(GROUPS_BASE_V1, {
        id: props.id,
        name: props.name,
        displayName: props.displayName,
      }),
    );
  }

  readonly users = {
    list: (id: number | string, props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${GROUPS_BASE_V2}/${id}/users`, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),
  };

  readonly roles = {
    list: (id: number | string, props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${GROUPS_BASE_V2}/${id}/roles`, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),
  };

  readonly userMaps = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(USER_MAP_BASE_V2, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${USER_MAP_BASE_V1}/${id}`)),

    remove: (props: { ids: Array<number | string> }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.request<unknown>('DELETE', USER_MAP_BASE_V1, {
          body: props.ids,
        }),
      ),

    create: (props: UserMapPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(USER_MAP_BASE_V1, [{ userId: props.userId, userGroupId: props.userGroupId }])),

    batchCreate: (props: { mappings: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(USER_MAP_BASE_V1, props.mappings)),

    update: (props: UserMapPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(USER_MAP_BASE_V1, {
          id: props.id,
          userId: props.userId,
          userGroupId: props.userGroupId,
        }),
      ),
  };

  readonly roleMaps = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(ROLE_MAP_BASE_V2, {
          params: buildParams(props, PAGINATION_SEARCH_FIELDS),
        }),
      ),

    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${ROLE_MAP_BASE_V1}/${id}`)),

    remove: (props: { ids: Array<number | string> }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.request<unknown>('DELETE', ROLE_MAP_BASE_V1, {
          body: props.ids,
        }),
      ),

    create: (props: RoleMapPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(ROLE_MAP_BASE_V1, [{ roleId: props.roleId, userGroupId: props.userGroupId }])),

    batchCreate: (props: { mappings: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.post<unknown>(ROLE_MAP_BASE_V1, props.mappings)),

    update: (props: RoleMapPayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(ROLE_MAP_BASE_V1, {
          id: props.id,
          roleId: props.roleId,
          userGroupId: props.userGroupId,
        }),
      ),
  };
}
